const DEFAULT_GRID_SIZE = 0.25;
const DEFAULT_ENDPOINT_THRESHOLD = 0.32;

function finitePoint(point) {
  if (!Array.isArray(point) || point.length < 2) return null;
  const x = Number(point[0]);
  const z = Number(point[1]);
  return Number.isFinite(x) && Number.isFinite(z) ? [x, z] : null;
}

export class SnapManager {
  constructor({ gridSize = DEFAULT_GRID_SIZE, endpointThreshold = DEFAULT_ENDPOINT_THRESHOLD } = {}) {
    this.gridSize = Math.max(0.05, Number(gridSize) || DEFAULT_GRID_SIZE);
    this.endpointThreshold = Math.max(0.05, Number(endpointThreshold) || DEFAULT_ENDPOINT_THRESHOLD);
    this.enabled = true;
  }

  setEnabled(enabled) {
    this.enabled = Boolean(enabled);
  }

  snapValue(value) {
    return Math.round(Number(value) / this.gridSize) * this.gridSize;
  }

  snapPoint(point, { reference = null, endpoints = [], angleStep = Math.PI / 4 } = {}) {
    const source = finitePoint(point);
    if (!source) throw new Error('Snap point must contain finite x and z values.');
    if (!this.enabled) return { point: source, kind: 'free' };

    let candidate = source;
    const origin = finitePoint(reference);
    if (origin) {
      const dx = candidate[0] - origin[0];
      const dz = candidate[1] - origin[1];
      const length = Math.hypot(dx, dz);
      if (length > 0.001) {
        const angle = Math.round(Math.atan2(dz, dx) / angleStep) * angleStep;
        candidate = [origin[0] + Math.cos(angle) * length, origin[1] + Math.sin(angle) * length];
      }
    }

    candidate = [this.snapValue(candidate[0]), this.snapValue(candidate[1])];
    let nearest = null;
    let nearestDistance = this.endpointThreshold;
    for (const endpoint of endpoints) {
      const valid = finitePoint(endpoint);
      if (!valid) continue;
      const distance = Math.hypot(valid[0] - candidate[0], valid[1] - candidate[1]);
      if (distance <= nearestDistance) {
        nearest = valid;
        nearestDistance = distance;
      }
    }
    return nearest
      ? { point: [...nearest], kind: 'endpoint', distance: nearestDistance }
      : { point: candidate, kind: origin ? 'angle-grid' : 'grid' };
  }

  projectToWall(point, walls, maxDistance = 0.6) {
    const source = finitePoint(point);
    if (!source) return null;
    let closest = null;
    for (const wall of walls ?? []) {
      const start = finitePoint(wall.start);
      const end = finitePoint(wall.end);
      if (!start || !end) continue;
      const dx = end[0] - start[0];
      const dz = end[1] - start[1];
      const lengthSquared = dx * dx + dz * dz;
      if (lengthSquared < 0.0001) continue;
      const t = Math.max(0, Math.min(1, ((source[0] - start[0]) * dx + (source[1] - start[1]) * dz) / lengthSquared));
      const projected = [start[0] + dx * t, start[1] + dz * t];
      const distance = Math.hypot(source[0] - projected[0], source[1] - projected[1]);
      if (distance <= maxDistance && (!closest || distance < closest.distance)) {
        closest = { wallId: wall.id, point: projected, distance, t, angle: Math.atan2(dz, dx) };
      }
    }
    return closest;
  }

  validateWall(start, end, { minimum = 0.25, maximum = 100 } = {}) {
    const a = finitePoint(start);
    const b = finitePoint(end);
    if (!a || !b) return { valid: false, reason: 'Wall coordinates are invalid.', length: 0 };
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (length < minimum) return { valid: false, reason: `Wall must be at least ${minimum} m.`, length };
    if (length > maximum) return { valid: false, reason: `Wall cannot exceed ${maximum} m.`, length };
    return { valid: true, reason: '', length };
  }
}

